import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from '@contexts/AuthContext'
import { ThemeProvider } from '@contexts/ThemeContext'
import { TimerProvider } from '@contexts/TimerContext'
import { SystemProvider } from '@contexts/SystemContext'
import Layout from '@components/Layout/Layout'
import ProtectedRoute from '@components/Auth/ProtectedRoute'

// PWA Components
import { InstallPrompt } from '@components/PWA/InstallPrompt'
import { OfflineIndicator } from '@components/PWA/OfflineIndicator'

// Pages
import LoginPage from '@pages/Auth/LoginPage'
import TimerPage from '@pages/Timer/TimerPage'
import ProjectsPage from '@pages/Projects/ProjectsPage'
import TimesheetsPage from '@pages/Timesheets/TimesheetsPage'
import TimesheetDetailPage from '@pages/Timesheets/TimesheetDetailPage'
import TimesheetEditPage from '@pages/Timesheets/TimesheetEditPage'
import ApprovalsPage from '@pages/Approvals/ApprovalsPage'
import ApprovalDetailPage from '@pages/Approvals/ApprovalDetailPage'
import ReportsPage from '@pages/Reports/ReportsPage'
import SettingsPage from '@pages/Settings/SettingsPage'
import UsersPage from '@pages/Users/UsersPage'
import NotificationsPage from '@pages/Notifications/NotificationsPage'
import PartnerMapPage from '@pages/PartnerMap/PartnerMapPage'
import NotFoundPage from '@pages/NotFound/NotFoundPage'

function App() {
  return (
    <ThemeProvider>
      <SystemProvider>
        <AuthProvider>
          <TimerProvider>
            <Router>
          <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <TimerPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/timer" element={
                <ProtectedRoute>
                  <Layout>
                    <TimerPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/projects" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <ProjectsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/timesheets" element={
                <ProtectedRoute>
                  <Layout>
                    <TimesheetsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/timesheets/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <TimesheetDetailPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/timesheets/:id/edit" element={
                <ProtectedRoute>
                  <Layout>
                    <TimesheetEditPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/approvals" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <ApprovalsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/approvals/:id" element={
                <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                  <Layout>
                    <ApprovalDetailPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Layout>
                    <ReportsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/users" element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <UsersPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout>
                    <SettingsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Layout>
                    <NotificationsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/partner-map" element={
                <ProtectedRoute>
                  <Layout>
                    <PartnerMapPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            
            {/* PWA Components */}
            <OfflineIndicator />
            <InstallPrompt />
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#ffffff',
                  color: '#1e293b',
                  border: '2px solid #fef3c7',
                  borderRadius: '12px',
                  boxShadow: '0 4px 25px -5px rgba(245, 158, 11, 0.15), 0 10px 30px -5px rgba(245, 158, 11, 0.08)',
                  fontWeight: '500',
                },
                success: {
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#ffffff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#ffffff',
                  },
                },
              }}
            />
          </div>
            </Router>
          </TimerProvider>
        </AuthProvider>
      </SystemProvider>
    </ThemeProvider>
  )
}

export default App
